import { Component, Injector, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { InteractionService } from './service/interaction/interaction.service';
import { UserProfile } from './model/rolePlay.model';
import { ChatService } from './service/chat-service';
import { MatDialog } from '@angular/material/dialog';
import { VideoConfirmationDialogComponent } from '../app/components/video-confirmation-dialog/video-confirmation-dialog.component';
import { TraineeListService } from './service/trainee-list.service';
import { TextToSpeechService } from './service/text-to-speech/text-to-speech.service';
import { EncryptionService } from './service/encryption/encryption.service';
import { IdealTimeOutService } from './service/ideal-time-out.service';
import { MaintenanceDialogService } from './service/shared/maintenance-dialog.service';
import { Role, UserProfileResponse } from './model/shared/user.model';
import { ProductPlatform } from './model/shared/platform-region.model';
import { PlatformRegionService } from './service/shared/platform-region.service';
import { KeyVaultService } from './service/key-vault.service';
import { OnboardingApiService } from './v2/services/onboarding/onboarding-api.service';
import { ToastrService } from 'ngx-toastr';
import { AppLaunchPromptService } from './service/app-launch-prompt.service';
import { AppLaunchPromptDialogComponent } from './components/app-launch-prompt-dialog/app-launch-prompt-dialog.component';
const GRAPH_ENDPOINT = 'https://graph.microsoft.com/v1.0/me';


type ProfileType = {
  givenName?: string,
  surname?: string,
  userPrincipalName?: string,
  id?: string,
  displayName?: string,
  mail?: string
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'jnj-aiadvisor-ui';
  isIframe = false;
  loginDisplay = false;
  displayName: string = "";
  profile!: ProfileType;
  isLoaded: boolean = false;
  get layoutState() {
    return this.intService.layoutState;
  }
  userProfile!: UserProfile;
  appComponentLoaded: boolean = false;
  private lastLoginUpdated = false;
  isProductList: boolean = false;
  ADGroupPlusProduct: Array<string> = [];
  public videoUrl: string = ''; // Replace with actual video URL
  private readonly _destroying$ = new Subject<void>();
  private authChannel = new BroadcastChannel('auth_channel'); // 🔹 Add BroadcastChannel
  idealTimeOutService: IdealTimeOutService
  bannerMessage: string = '';

  constructor(private readonly http: HttpClient, private readonly broadcastService: MsalBroadcastService,
    private readonly authService: MsalService, private readonly router: Router, private readonly intService: InteractionService,
    private readonly chatService: ChatService, public dialog: MatDialog, private readonly traineeListService: TraineeListService, private readonly textToSpeechService: TextToSpeechService,
    private readonly encryptionService: EncryptionService, private readonly injector: Injector, private readonly toastr: ToastrService,
    private maintenanceDialogService: MaintenanceDialogService,
    private platformRegionService: PlatformRegionService,
    private readonly keyVaultService: KeyVaultService,
    private onboardingApiService: OnboardingApiService,
    private readonly appLaunchPromptService: AppLaunchPromptService
  ) {
    this.authChannel.onmessage = (message) => {
      if (message.data.type === 'token') {
        localStorage.setItem('bearer_token', message.data.token);
      } else if (message.data.type === 'request_token') {
        const token = localStorage.getItem('bearer_token');
        if (token) {
          this.authChannel.postMessage({ type: 'token', token });
        }
      } else if (message.data === 'logout') {
        sessionStorage.clear();
        localStorage.clear();
        window.location.reload();
      }
    };

    if (!localStorage.getItem('bearer_token')) {
      this.authChannel.postMessage({ type: 'request_token' });
    }
  }
  ngOnInit() {
    this.startApp()
  }

  // Prompts iOS browser users to continue in-browser or open the native app (no Android app exists yet),
  // remembering their choice so they are only asked once per device.
  checkAppLaunchPreference(): void {
    const platform = this.appLaunchPromptService.getPlatform();
    if (platform !== 'ios') {
      return;
    }
    const storedPreference = this.appLaunchPromptService.getStoredPreference();
    if (storedPreference === 'browser') {
      return;
    }
    if (storedPreference === 'app') {
      this.appLaunchPromptService.openApp(platform);
      return;
    }
    const dialogRef = this.dialog.open(AppLaunchPromptDialogComponent, { width: '360px', disableClose: true });
    dialogRef.afterClosed().subscribe((choice: 'app' | 'browser') => {
      if (!choice) {
        return;
      }
      this.appLaunchPromptService.setStoredPreference(choice);
      if (choice === 'app') {
        this.appLaunchPromptService.openApp(platform);
      }
    });
  }

  startApp() {
    this.isIframe = window !== window.parent && !window.opener;
    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        if (!this.loginDisplay) {
          this.login();
        } else {
          this.getProfile();
          // Prompt only once SSO confirms the user is actually logged in.
          this.checkAppLaunchPreference();
        }
      });

    //this.intService.triggerSpeechRecognition();
    this.intService.isProductSelected.subscribe(res => {
      if (res) {
        this.isProductList = false;
        this.appComponentLoaded = true;
        // Single entry point for every key vault secret; consumers read keyVaultService.secrets$.
        // Every product-selection path emits here once businessUnitCode is in session storage,
        // and the service caches per business unit, so repeat emissions cost nothing.
        this.keyVaultService.loadSecrets().subscribe({
          error: () => console.error('Failed to resolve key vault secrets.')
        });
      }
    })

    this.traineeListService.sessionTimeout$.subscribe(res => {
      if (res) {
        setTimeout(() => {
          if ((sessionStorage.getItem("idle_minutes") !== "null") && (sessionStorage.getItem("idle_minutes") !== "") && (sessionStorage.getItem("idle_minutes") !== " ")) {
            this.idealTimeOutService = this.injector.get<IdealTimeOutService>(IdealTimeOutService);
          }
        }, 10000);
      }
    })
  }

  logout() {
    this.authChannel.postMessage('logout');
    // Clear MSAL account cache to prevent stale session on re-login
    const accounts = this.authService.instance.getAllAccounts();
    accounts.forEach(account => {
      this.authService.instance.setActiveAccount(null);
    });
    this.authService.instance.clearCache();
    sessionStorage.clear();
    localStorage.clear();
    window.location.reload();
  }
  private setInitialPage(role) {
    this.maintenanceDialogService.checkScheduleOutage().then((isScheduleOutage: boolean) => { });
    if (sessionStorage.getItem('newtab') === 'yes') {
      this.router.navigate(['/app-doc-viewer'], { skipLocationChange: true });
    } else {
      this.router.navigate(['/app-abiomed-chat'], { skipLocationChange: true });
    }
    this.appComponentLoaded = true;
  }
  openVideoConfirmationDialog(): void {
    const dialogRef = this.dialog.open(VideoConfirmationDialogComponent, {
      width: '500px',
      height: '500px',
      data: { videoUrl: this.videoUrl }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.playVideo(this.videoUrl);
      }
    });
  }
  playVideo(videoUrl: string): void {
    window.open(videoUrl, '_blank');
  }
  login() {
    this.authService.loginRedirect();
    this.authChannel.postMessage('login'); // 🔹 Notify other tabs about login

  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  getGroups(profile: ProfileType) {
    // MSAL's own token cache now lives in localStorage (cacheLocation: LocalStorage)
    for (let key in localStorage) {
      if (key.includes('idtoken')) {
        let obj = localStorage.getItem(key);
        let jsonObj = JSON.parse(obj);
        const helper = new JwtHelperService();
        const decodedToken = helper.decodeToken(jsonObj.secret);
        const employeeId = decodedToken.employeeId;
        const groups = decodedToken.groups;
        sessionStorage.setItem('userId', employeeId);
        sessionStorage.setItem('userEmail', profile.mail);
        localStorage.setItem('bearer_token', jsonObj.secret);
        this.authChannel.postMessage({ type: 'token', token: jsonObj.secret });

        this.userProfile = new UserProfile();
        this.userProfile.wwid = employeeId;
        this.userProfile.email = profile.mail;
        this.userProfile.first_name = profile.givenName;
        this.userProfile.last_name = profile.surname;
        this.userProfile.created_by = employeeId;
        this.userProfile.user_type = "";

        if (groups != '' && groups != null && groups != 'undefined') {
          //if (false) {
          const roles = groups.join(',');
          const encryptText = this.encryptionService.encrypt(JSON.stringify(groups));
          sessionStorage.setItem('groups', encryptText);
          //here check condition error unauth page
          this.createUserProfile(this.userProfile, groups);
          this.ADGroupPlusProduct.push(roles);
          this.isLoaded = true;
          // setTimeout(()=> {
          //   this.textToSpeechService.initializeTTS();
          // }, 1000);

        } else {
          this.intService.hideComponentHandler({ hideHeader: true });
          this.router.navigate(['/app-unauthorizeduser'], { skipLocationChange: true });
        }
      }
    }
  }



  navigate(role) {
    if (sessionStorage.getItem('newtab') === 'yes') {
      this.router.navigate(['/app-doc-viewer'], { skipLocationChange: true });
    } else {
      this.router.navigate(['/app-pre-login'], { skipLocationChange: true });
      const wwid = sessionStorage.getItem('userId');
      this.updateLastLogin(wwid);
    }
    this.appComponentLoaded = true;
  }
  updateLastLogin(wwid: string): void {
    // The startup flow reaches this from two paths (navigate + profile
    // load), which sent the same PUT twice per page load. Recording the
    // login once per load is the intent, so subsequent calls are no-ops.
    if (this.lastLoginUpdated) {
      return;
    }
    this.lastLoginUpdated = true;
    this.chatService.getlastLogin(wwid).subscribe(
      response => {
        console.log('Last login updated successfully:', response);
      },
      error => {
        // Allow a retry from the other startup path if this attempt failed.
        this.lastLoginUpdated = false;
        console.error('Error updating last login:', error);
      }
    );
  }

  getProfile() {
    this.http.get(GRAPH_ENDPOINT)
      .subscribe(profile => {
        this.profile = profile;
        sessionStorage.setItem("profile", JSON.stringify(this.profile));
        sessionStorage.setItem("givenName", this.profile.givenName);
        sessionStorage.setItem("displayName", this.profile.displayName);
        const namePart = (this.profile.displayName || '').replace(/\s*\[.*?\]\s*$/, '');
        const parts = namePart.split(',').map(p => p.trim());
        const fullName = parts.length > 1 ? `${parts[1]} ${parts[0]}` : namePart;
        sessionStorage.setItem("fullName", fullName);
        this.getGroups(this.profile);
      });
  }
  createUserProfile(userProfileRequest: UserProfile, userRoles: any) {
    //check if use profile already exist


    this.chatService.getUserProfileByWWID().subscribe((response: UserProfileResponse) => {
      if (response?.message) {
        sessionStorage.setItem('onboarding_banner_message', response.message);
        const confluenceLink = (response as any)?.confluence_link;
        if (confluenceLink) {
          sessionStorage.setItem('onboarding_banner_confluence', confluenceLink);
        }
        this.intService.hideComponentHandler({ hideHeader: true });
        this.isLoaded = false;
        this.router.navigate(['/app-unauthorizeduser'], { skipLocationChange: true });
        return;
      }
      if (response && response.wwid == userProfileRequest.wwid) {
        sessionStorage.setItem('role', response.user_type);
        this.platformRegionService.setUserDefaultData(response);
        this.platformRegionService.setIntialProductPlatform()
        this.intService.setProduct((response.product.length) ? response.product : []);
        this.updateLastLogin(sessionStorage.getItem('userId'));
        if (this.platformRegionService.selectedProductPlatform) {
          this.loadSelectedProduct(this.platformRegionService.selectedProductPlatform);
        } else {
          this.isProductList = true;
          this.router.navigate(['/app-product-list'], { skipLocationChange: true });
        }
        if (response.firsttime_flag === 'N') {
          this.openVideoConfirmationDialog();
        }

      } else if (response && response.wwid) {
        this.chatService.createUserProfile(userProfileRequest)
          .subscribe({
            next: (res: any) => {
              console.log('profile creation done');
            },
            error: (httpError: HttpErrorResponse) => {
              const error: any | null = httpError.error;
              const errorCode: number = httpError.status;
              console.error(`Returned error ${error} with status code ${errorCode}`);
            }
          })
      }
    });
  }

  loadSelectedProduct(productPlatform: ProductPlatform) {
    const role: Role = this.platformRegionService.selectedRole;
    sessionStorage.setItem('displayRole', role.role_label); // Role Lable
    sessionStorage.setItem('role', role.role_name); // Role Name
    sessionStorage.setItem('roleCode', role.role_code); // Role code
    sessionStorage.setItem('businessUnitCode', productPlatform.business_unit_code); // Product Code
    sessionStorage.setItem('businessUnit', productPlatform.business_unit); // Product Name
    // sessionStorage.setItem('primaryMarketEnabled', defaultProduct.is_primary_market_enabled); // Product Name
    // sessionStorage.setItem('defaultPrimaryMarketEnabled', defaultProduct.is_primary_market_enabled); // Product Name
    sessionStorage.setItem('defaultMarket', this.platformRegionService.default_market_code)
    sessionStorage.setItem('defaultRegion', this.platformRegionService.default_region_code)
    sessionStorage.setItem('defaultProduct', this.platformRegionService.default_platform_code);
    this.intService.isProductSelected.next(true);
    this.userProfile.user_type = role.role_name;
    this.navigate(role.role_name);
    this.setInitialPage(role.role_name);
    this.isProductList = false;
    this.textToSpeechService.initializeTTS();
  }

  getCustomizeDetails() {
    this.traineeListService.getCustomizeDetails().subscribe({
      next: (data: any) => {
        if (data) {
          this.textToSpeechService.textToSpeechEnabled$.next(data.enable_voice_interaction);

        }
      }
    })
  }
}